import { useState, useEffect } from 'react';
import { Database, Folder, FolderOpen, ShieldCheck, UserCheck, Layers, Hash, Loader2, Edit, Trash2, PlusCircle } from 'lucide-react';
import { useOntologyStore } from '@/stores/useOntologyStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type { OntologyConcept } from '@/types/ontology';
import { ConceptDialog } from './ConceptDialog';
import { DeleteConceptDialog } from './DeleteConceptDialog';

export function OntologyBrowserPanel() {
  const { activeWorkspace } = useWorkspaceStore();
  const { concepts, loading, error, fetchConcepts } = useOntologyStore();
  const [localError, setLocalError] = useState<string | null>(null);
  
  // Dialog state
  const [isConceptDialogOpen, setIsConceptDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedConcept, setSelectedConcept] = useState<OntologyConcept | null>(null);

  // Fetch concepts when domain changes
  useEffect(() => {
    if (activeWorkspace?.domain?.id) {
      setLocalError(null);
      fetchConcepts(activeWorkspace.domain.id, null).catch((err) => {
        setLocalError(err.message || 'Failed to load ontology concepts');
      });
    }
  }, [activeWorkspace?.domain?.id, fetchConcepts]);

  const handleRefresh = () => {
    if (activeWorkspace?.domain?.id) {
      fetchConcepts(activeWorkspace.domain.id, null);
    }
  };

  const handleAddConcept = () => {
    setSelectedConcept(null);
    setIsConceptDialogOpen(true);
  };

  const handleEditConcept = (concept: OntologyConcept) => {
    setSelectedConcept(concept);
    setIsConceptDialogOpen(true);
  };

  const handleDeleteConcept = (concept: OntologyConcept) => {
    setSelectedConcept(concept);
    setIsDeleteDialogOpen(true);
  };

  // Group concepts by status
  const coreTerms = concepts.filter((c) => c.status === 'CORE');
  const approvedTerms = concepts.filter((c) => c.status === 'APPROVED');
  // Note: Current OntologyStatus only has CORE|APPROVED, no DRAFT status exists
  const draftTerms: OntologyConcept[] = [];

  const renderTermList = (items: OntologyConcept[], colorClass: string, icon: React.ReactNode, badgeColor: string, editable: boolean = false) => (
    <div className="space-y-1 mt-2">
      {items.length === 0 && <div className="text-[10px] text-muted-foreground italic pl-6">Empty layer</div>}
      {items.map((item) => (
        <div key={item.id} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-muted/50 transition-colors group cursor-default">
          <span className={`opacity-70 group-hover:opacity-100 ${colorClass}`}>{icon}</span>
          <span className="text-foreground font-mono">{item.name}</span>
          <span className={`ml-auto text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded ${badgeColor}`}>
            {item.metaType}
          </span>
          {editable && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleEditConcept(item)}
                className="p-1 hover:bg-blue-100 rounded transition-colors"
                title="Edit concept"
              >
                <Edit size={12} className="text-blue-600" />
              </button>
              <button
                onClick={() => handleDeleteConcept(item)}
                className="p-1 hover:bg-red-100 rounded transition-colors"
                title="Delete concept"
              >
                <Trash2 size={12} className="text-red-600" />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );


  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-border bg-muted/30 backdrop-blur-sm">
        <h3 className="text-sopho-entity font-bold uppercase text-[10px] tracking-widest flex items-center gap-2">
          <Database size={14} /> Ontology Browser
        </h3>
      </div>

      <div className="p-3 border-b border-border">
        <div className="flex justify-between items-center mb-2">
          <label className="text-[10px] text-muted-foreground font-bold uppercase">Active Domain Scope</label>
          <button
            onClick={handleAddConcept}
            title="Add New Concept"
            className="text-muted-foreground hover:text-sopho-entity transition-colors"
            disabled={!activeWorkspace?.domain?.id}
          >
            <PlusCircle size={14} />
          </button>
        </div>
        <div className="py-1.5 px-2 rounded text-[10px] font-bold uppercase tracking-wide border bg-sopho-entity/10 text-sopho-entity border-sopho-entity/30">
          {activeWorkspace?.domain?.label || 'No domain selected'}
        </div>
        {activeWorkspace?.domain?.coreOntologyName && (
          <p className="text-[10px] text-muted-foreground mt-2 truncate">{activeWorkspace.domain.coreOntologyName}</p>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <Loader2 size={24} className="animate-spin text-sopho-entity" />
            <p className="text-xs text-muted-foreground">Loading ontology...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {(error || localError) && !loading && (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-xs text-red-500 font-semibold mb-1">Failed to load ontology</p>
            <p className="text-[10px] text-muted-foreground">{error || localError}</p>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && !error && !localError && (
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          <div>
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <ShieldCheck size={14} />
              <h4 className="text-xs font-bold uppercase tracking-wider">L1: System Core (Read-Only)</h4>
            </div>
            <div className="border-l-2 border-blue-200 pl-2 ml-1.5">
              {renderTermList(coreTerms, 'text-blue-400', <Hash size={12} />, 'bg-blue-100 text-blue-700 border border-blue-300', false)}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <UserCheck size={14} />
              <h4 className="text-xs font-bold uppercase tracking-wider">L2: Customer Baseline (Editable)</h4>
            </div>
            <div className="border-l-2 border-emerald-200 pl-2 ml-1.5 bg-emerald-500/5 rounded-r-lg">
              {renderTermList(approvedTerms, 'text-green-400', <FolderOpen size={12} />, 'bg-green-100 text-green-700 border border-green-300', true)}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Layers size={14} />
              <h4 className="text-xs font-bold uppercase tracking-wider">L3: User Drafts (Editable)</h4>
            </div>
            <div className="border-l-2 border-amber-200 pl-2 ml-1.5">
              {renderTermList(draftTerms, 'text-yellow-400', <Folder size={12} />, 'bg-amber-100 text-amber-700 border border-amber-300', true)}
            </div>
          </div>
        </div>
      )}

      <div className="p-3 border-t border-border bg-muted/30 backdrop-blur-sm text-[9px] text-muted-foreground flex flex-col gap-1">
        <div className="flex justify-between">
          <span>Total Concepts: {concepts.length}</span>
          <span className="text-sopho-entity font-semibold">CORE: {coreTerms.length} | APPROVED: {approvedTerms.length} | DRAFT: {draftTerms.length}</span>
        </div>
        <div className="w-full bg-muted h-1 rounded overflow-hidden flex mt-1">
          <div className="bg-blue-500" style={{ width: `${concepts.length > 0 ? (coreTerms.length / concepts.length) * 100 : 0}%` }} />
          <div className="bg-green-500" style={{ width: `${concepts.length > 0 ? (approvedTerms.length / concepts.length) * 100 : 0}%` }} />
          <div className="bg-yellow-500" style={{ width: `${concepts.length > 0 ? (draftTerms.length / concepts.length) * 100 : 0}%` }} />
        </div>
      </div>

      {/* CRUD Dialogs */}
      <ConceptDialog
        open={isConceptDialogOpen}
        onOpenChange={setIsConceptDialogOpen}
        domainId={activeWorkspace?.domain?.id || ''}
        concept={selectedConcept}
        token={null}
        onSuccess={handleRefresh}
      />
      <DeleteConceptDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        concept={selectedConcept}
        token={null}
        onSuccess={handleRefresh}
      />
    </div>
  );
}
